import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import ServiceTypes from "../../../service/ServiceTypes";
import AttToken from "../../../helpers/attToken";
import Clients from "../../../service/Clients";
import {
  Autocomplete,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  FormControl,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  TextField,
  FormGroup,
  Checkbox,
  IconButton,
  Typography,
  Tabs,
  Box,
  Tab,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import NovoChamado from "../../../service/NovoChamado";
import { toast } from "react-toastify";
import queryClient from "../../../service/query";
import Users from "../../../service/Users";
import { userBackObj } from "../../Users/ListaUsuarios";
import { trackEventMatomo } from "../../../helpers/matomo";
import GenericAcordonPlaces from "../../GenericAcordeonPlaces/GenericAcordeonPlaces";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import CloseIcon from "@mui/icons-material/Close";
import type { types } from "../../../types/services/types";
import type { Team } from "../../../types/teams/types";
import type { TicketModel, TicketModelPermissions, TicketPermissionRule, TicketPrioritySLADraft, TicketResponsibleType, newTicketObjType } from "../../../types/tickets/types";
import Teams from "../../../service/Teams";
import InfoIcon from "@mui/icons-material/Info";

type slaPriority = 'firstAttendance' | 'startProgress' | 'completion';

const emptyPrioritySLADraft = (): TicketPrioritySLADraft => ({
  low: { firstAttendance: null, startProgress: null, completion: null },
  medium: { firstAttendance: null, startProgress: null, completion: null },
  high: { firstAttendance: null, startProgress: null, completion: null },
});

const emptyPermissions = (): TicketModelPermissions => ({
  rejectRule: null,
  cancelRule: null,
  completionRule: null,
  closeRule: null,
  reopenRule: null,
  aproveRule: null,
  changePriorityRule: null,
});

export type { newTicketObjType } from '../../../types/tickets/types';

type props = {
  isEdit?: boolean;
  setIsEdit?: Function;
  ticketEdit?: newTicketObjType | undefined;
  setTicketEdit: Function;
  openModal: boolean;
  setOpenModal: Function;
};

const serviceType = new ServiceTypes();
const CustomerService = new Clients();
const chamadoService = new NovoChamado();
const usersService = new Users();
const teamsService = new Teams();

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function AutocompleteResponsavel({
  data,
  isLoading,
  setValue,
  responsavel,
  label,
}: {
  data: { value: string; label: string }[];
  isLoading: boolean;
  setValue: Function;
  responsavel: string | null;
  label: string;
}) {
  return isLoading ? (
    <Skeleton variant="rounded" width={"100%"} height={50} />
  ) : (
    <Autocomplete
      isOptionEqualToValue={(option, value) => option.value === value.value}
      onClick={() => trackEventMatomo("modal criação de chamado", "click", "select", "seleciona valor")}
      value={
        responsavel
          ? {
            value: responsavel,
            label: data?.find((ele: any) => ele.value === responsavel)?.label ?? "",
          }
          : null
      }
      filterSelectedOptions
      loading={isLoading}
      disabled={isLoading}
      options={data ?? []}
      onChange={(_event: any, newValue: { label: string; value: string } | null, reason: string) => {
        if (newValue) {
          setValue(newValue?.value);
        }
        if (reason === "clear") {
          setValue(null);
        }
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.value}>
          <span>{option.label}</span>
        </li>
      )}
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
}

function SelectResponsavel({
  label,
  value,
  setValue,
  onLoading,
  disabled = false,
  ticketEdit,
}: {
  label: string;
  value: TicketPermissionRule | null | undefined;
  setValue: (value: TicketPermissionRule | null) => void;
  onLoading: boolean;
  disabled?: boolean;
  ticketEdit: newTicketObjType | undefined;
}) {
  const { t } = useTranslation("translation");
  const permsionByRole = {
    responsible: t("notifications.ticket.responsible").replace(":", ""),
    responsibleTeamSuperOnly: t("notifications.ticket.responsibleTeamSuperOnly"),
    responsibleTeamOnly: t("notifications.ticket.responsibleTeamOnly"),
    teamSuperOnly: t("notifications.ticket.teamSuperOnly"),
    team: t("notifications.ticket.teamFor"),
    everyone: t("notifications.ticket.everyone"),
  };
  return (
    <FormControl style={{ width: label === t("notifications.ticket.canChangePriority") ? '98%' : '48%' }}>
      <InputLabel>{label}</InputLabel>
      <Select disabled={onLoading || disabled} value={value ?? ""} label={label} onChange={(e) => setValue((e.target.value || null) as TicketPermissionRule | null)}>
        <MenuItem value="">Padrão da empresa</MenuItem>
        {Array.from(Object.entries(permsionByRole))
          .filter(([key]) => {
            const responsibleType = ticketEdit?.responsible?.type;
            if (responsibleType === "team") return key !== "responsible";
            if (responsibleType === "user") return !(key === "responsibleTeamOnly" || key === "responsibleTeamSuperOnly");
            return key === "team" || key === "teamSuperOnly" || key === "everyone";
          })
          ?.map(([key, label]) => (
            <MenuItem key={key} value={key}>
              {label}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  );
}

function SLAInput({
  slaKey,
  label,
  onChange,
  valor
}: {
  slaKey: slaPriority;
  label: string;
  onChange: (valor: number, key: slaPriority) => void; valor: number | null;
}) {
  const { t } = useTranslation("translation");
  const [SLA, setSLA] = useState({
    dia: "",
    hora: "",
    min: "",
  });

  useEffect(() => {
    if (valor !== null && valor !== undefined) {
      const dias = Math.floor(valor / (24 * 60 * 60 * 1000));
      const horas = Math.floor((valor % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
      const minutos = Math.floor((valor % (60 * 60 * 1000)) / (60 * 1000));
      setSLA({
        dia: dias.toString(),
        hora: horas.toString(),
        min: minutos.toString(),
      });
    } else {
      setSLA({ dia: "", hora: "", min: "" });
    }
  }, [valor]);

  function handleValues(key: string, val: string) {
    const novoSLA = {
      ...SLA,
      [key]: val,
    };

    setSLA(novoSLA);

    const dias = Number(novoSLA.dia || 0);
    const horas = Number(novoSLA.hora || 0);
    const minutos = Number(novoSLA.min || 0);

    const total = dias * 24 * 60 * 60 * 1000 + horas * 60 * 60 * 1000 + minutos * 60 * 1000;

    onChange(total, slaKey);
  }

  function calculateValue() {
    trackEventMatomo("modal criação de chamado", "click", "botão", "ajusta SLA");
    let dias = parseInt(SLA.dia || "0");
    let horas = parseInt(SLA.hora || "0");
    let minutos = parseInt(SLA.min || "0");

    horas += Math.floor(minutos / 60);
    minutos = minutos % 60;

    dias += Math.floor(horas / 24);
    horas = horas % 24;

    const novoValor = {
      dia: dias.toString(),
      hora: horas.toString(),
      min: minutos.toString(),
    };

    setSLA((prev) => ({
      ...prev,
      ...novoValor,
    }));

    const timestamp = dias * 24 * 60 * 60 * 1000 + horas * 60 * 60 * 1000 + minutos * 60 * 1000;
    onChange(timestamp, slaKey);
  }

  function disabled(): boolean {
    if (SLA.dia === "" && SLA.hora === "" && SLA.min === "") return true;
    if (isNaN(Number(SLA.dia)) || isNaN(Number(SLA.hora)) || isNaN(Number(SLA.min))) return true;
    if (Number(SLA.dia) < 0 || Number(SLA.hora) < 0 || Number(SLA.min) < 0) return true;
    if (Number(SLA.hora) >= 24 || Number(SLA.min) >= 60) return false;
    return false;
  }

  const infoText = t("notifications.ticket.infoText");
  return (
    <Card>
      <CardContent>
        <Stack direction="column" justifyContent="flex-start" alignItems="stretch" spacing={2}>
          <Typography gutterBottom sx={{ color: "text.secondary", fontSize: 14 }}>
            {label}
          </Typography>
          <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={1} width={"100%"}>
            <TextField
              type="text"
              variant="standard"
              label={`${t("formDetails.GeneralTab.dias").replace("s", "").toLowerCase()}(s)`}
              slotProps={{
                htmlInput: { maxLength: 3 },
                input: {
                  style: { textAlign: "center" },
                },
              }}
              value={SLA.dia ?? ""}
              onChange={(e) => {
                if (e.target.value === "" || isNaN(Number(e.target.value))) {
                  handleValues("dia", "");
                  return;
                }
                handleValues("dia", e.target.value);
              }}
            />{" "}
            -
            <TextField
              variant="standard"
              label={`${t("hour").toLowerCase()}(s)`}
              slotProps={{
                htmlInput: { maxLength: 2 },
                input: {
                  style: { textAlign: "center" },
                },
              }}
              value={SLA.hora ?? ""}
              onChange={(e) => {
                if (e.target.value === "" || isNaN(Number(e.target.value))) {
                  handleValues("hora", "");
                  return;
                }
                handleValues("hora", e.target.value);
              }}
            />{" "}
            :
            <TextField
              variant="standard"
              label={t("minutos")}
              slotProps={{
                htmlInput: { maxLength: 2 },
                input: {
                  style: { textAlign: "center" },
                },
              }}
              value={SLA.min ?? ""}
              onChange={(e) => {
                if (e.target.value === "" || isNaN(Number(e.target.value))) {
                  handleValues("min", "");
                  return;
                }
                handleValues("min", e.target.value);
              }}
            />
          </Stack>
        </Stack>
      </CardContent>
      <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ padding: 2, paddingTop: 0 }}>
        <Button onClick={calculateValue} disabled={disabled()}>
          {t("notifications.ticket.ajustTime")}
        </Button>
        <Tooltip title={infoText}>
          <InfoIcon />
        </Tooltip>
      </Stack>
    </Card>
  );
}

function BasicInfoTab({
  setTicketEdit,
  ticketEdit,
}: {
  setTicketEdit: Function;
  ticketEdit: newTicketObjType | undefined;
}) {
  const { t } = useTranslation("translation");
  const [cnjsArr, setCnjsArr] = useState(ticketEdit?.setsList || []);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["GET_SERVICES"],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error("Token not found");
        if (token) {
          const body = { ...token, empresa: token.userEmpresa };
          const result = await serviceType.getAllByCompany(body);
          if (!result) throw new Error("No response from server");
          return result;
        }
      } catch (err) {
        return err;
      }
    },
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const {
    data: places,
    isLoading: isPlacesLoading,
    isFetching: isPlacesFetching,
  } = useQuery({
    queryKey: ["GET_CLIENTES_CHILDREN", ticketEdit?.serviceType],
    queryFn: async () => {
      try {
        const token = await AttToken();
        if (!token) throw new Error("Token not found");
        if (token) {
          const response = await CustomerService.getAllWithSets(token, ticketEdit?.serviceType ?? "");
          if (!response) throw new Error("No response from server");
          return response;
        }
      } catch (err) {
        console.error(err);
      }
    },
    enabled: !!ticketEdit?.serviceType,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    retry: 1,
  });

  useEffect(() => {
    setTicketEdit((prev: any) => ({
      ...prev,
      setsList: cnjsArr,
    }));
  }, [cnjsArr, setTicketEdit]);

  const onLoading = isLoading || isFetching || isPlacesLoading || isPlacesFetching;

  return (
    <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
      <TextField
        onClick={() => trackEventMatomo("modal criação de chamado", "click", "input", "edita valor")}
        fullWidth
        value={ticketEdit?.name ?? ""}
        label={t("name")}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTicketEdit({ ...ticketEdit, name: e.target.value })}
      />
      <TextField
        onClick={() => trackEventMatomo("modal criação de chamado", "click", "input", "edita valor")}
        fullWidth
        multiline
        maxRows={4}
        value={ticketEdit?.message ?? ""}
        label={t("message")}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTicketEdit({ ...ticketEdit, message: e.target.value })}
      />
      <Stack direction="row" justifyContent="flex-start" alignItems="center" spacing={2}>
        {onLoading ? (
          <Skeleton variant="rounded" height={50} width={"100%"} />
        ) : (
          <Autocomplete
            fullWidth
            onClick={() => trackEventMatomo("modal criação de chamado", "click", "select", "seleciona valor")}
            disabled={ticketEdit?.serviceType !== ""}
            value={
              ticketEdit?.serviceType
                ? {
                  value: ticketEdit?.serviceType ?? "",
                  label: data?.find((ele: types) => ele.tipoId === ticketEdit?.serviceType)?.nome ?? "",
                }
                : null
            }
            filterSelectedOptions
            options={data
              ?.sort((a: types, b: types) => {
                return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1;
              })
              ?.map((ele: any) => ({
                label: ele.nome,
                value: ele.tipoId,
              }))}
            onChange={(_event: any, _newValue: { label: string; value: string } | null, _reason: any, details: any) => {
              setTicketEdit({ ...ticketEdit, serviceType: details.option.value });
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                <span>{option.label}</span>
              </li>
            )}
            renderInput={(params) => <TextField {...params} label={t("notifications.ticket.tipoServico")} />}
          />
        )}
        {ticketEdit?.serviceType !== "" && (
          <Button
            variant="contained"
            color="error"
            disabled={onLoading}
            endIcon={<CompareArrowsIcon />}
            onClick={() => {
              trackEventMatomo("modal criação de chamado", "click", "button", "desassocia tipo de serviço");
              setTicketEdit({ ...ticketEdit, serviceType: "", setsList: [] });
            }}>
            {t("formDetails.Association.desassociar")}
          </Button>
        )}
      </Stack>
      {ticketEdit && ticketEdit?.serviceType?.length > 0 && (
        <GenericAcordonPlaces
          isLoading={isPlacesLoading || isPlacesFetching}
          conjuntosArray={cnjsArr}
          setConjuntosArray={setCnjsArr}
          clients={places}
        />
      )}
    </Stack>
  );
}

function ConfigTicketTab({ setTicketEdit, ticketEdit }: { setTicketEdit: Function; ticketEdit: newTicketObjType | undefined }) {
  const { t } = useTranslation("translation");

  const results = useQueries({
    queries: [
      {
        queryKey: ["GET_USERS"],
        queryFn: async () => {
          try {
            const token = await AttToken();
            if (!token) throw new Error("Token not found");
            if (token) {
              const body = { ...token };
              const result = await usersService.getAllUsers(body);
              if (!result.users) throw new Error("No response from server");
              return result.users;
            }
          } catch (err) {
            return err;
          }
        },
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
        retry: 1,
      },
      {
        queryKey: ["GET_TEAMS"],
        queryFn: async () => {
          try {
            const token = await AttToken();
            if (!token) throw new Error("Token not found");
            if (token) {
              const body = { ...token };
              const result = await teamsService.getAllByCurrentUser(body);
              if (!result) throw new Error("No response from server");
              return result;
            }
          } catch (err) {
            return err;
          }
        },
        refetchOnMount: "always",
        refetchOnWindowFocus: false,
        retry: 1,
      },
    ],
  });

  const onLoading = results?.some((result: any) => result.isLoading) || false;
  const onFetching = results?.some((result: any) => result.isFetching) || false;

  const isLoading = onLoading || onFetching;

  function setResponsibleType(valor: TicketResponsibleType | "none") {
    setTicketEdit((prev: newTicketObjType) => ({
      ...prev,
      responsible: null,
      permissions: emptyPermissions(),
    }));
  }

  function setResponsible(valor: string) {
    setTicketEdit((prev: newTicketObjType) => ({
      ...prev,
      responsible: prev.responsible ? { ...prev.responsible, id: valor } : null,
    }));
  }

  function handlePermission(value: TicketPermissionRule | null, key: keyof TicketModelPermissions) {
    setTicketEdit((prev: newTicketObjType) => ({
      ...prev,
      permissions: { ...emptyPermissions(), ...prev.permissions, [key]: value },
    }));
  }

  function handlePriority(low: number | null, mid: number | null, hig: number | null, slaKey: string) {
    setTicketEdit((prev: newTicketObjType) => ({
      ...prev,
      prioritySLA: {
        ...(prev.prioritySLA || emptyPrioritySLADraft()),
        low: { ...(prev.prioritySLA || emptyPrioritySLADraft()).low, [slaKey]: low },
        medium: { ...(prev.prioritySLA || emptyPrioritySLADraft()).medium, [slaKey]: mid },
        high: { ...(prev.prioritySLA || emptyPrioritySLADraft()).high, [slaKey]: hig },
      },
    }));
  }

  return (
    <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={2}>
      <FormGroup>
        <FormControlLabel
          disabled={isLoading}
          control={
            <Checkbox
              checked={ticketEdit?.blockRefuse === true}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTicketEdit({ ...ticketEdit, blockRefuse: e.target.checked });
                trackEventMatomo("modal criação de chamado", "click", "input", "edita valor");
              }}
            />
          }
          label={t("notifications.ticket.blockRefuse")}
        />
        <FormControlLabel
          disabled={isLoading}
          control={
            <Checkbox
              checked={ticketEdit?.redirectable ?? false}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTicketEdit({ ...ticketEdit, redirectable: e.target.checked });
                trackEventMatomo("modal criação de chamado", "click", "input", "edita valor");
              }}
            />
          }
          label={t("notifications.ticket.redirectable")}
        />
        <FormControlLabel
          disabled={isLoading}
          control={
            <Checkbox
              checked={ticketEdit?.canReopen === true}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTicketEdit({ ...ticketEdit, canReopen: e.target.checked });
                trackEventMatomo("modal criação de chamado", "click", "input", "edita valor");
              }}
            />
          }
          label={t("notifications.ticket.canReopen")}
        />
        <FormControlLabel
          disabled={isLoading}
          control={
            <Checkbox
              checked={ticketEdit?.aprove === true}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setTicketEdit({ ...ticketEdit, aprove: e.target.checked });
                trackEventMatomo("modal criação de chamado", "click", "input", "edita valor");
              }}
            />
          }
          label={t("notifications.ticket.requiresApproval")}
        />
      </FormGroup>
      <Typography color="textSecondary" fontWeight="bold" fontSize="small">
        {t("notifications.ticket.responsable")}
      </Typography>
      <FormControl>
        <RadioGroup
          row
          value={ticketEdit?.responsible?.type ?? "none"}
          onChange={(eve) => {
            const responsibleType = eve.target.value as TicketResponsibleType | "none";
            setResponsibleType(responsibleType);
            if (responsibleType !== "none") {
              setTicketEdit((prev: newTicketObjType) => ({ ...prev, responsible: { type: responsibleType, id: "" } }));
            }
            trackEventMatomo("modal criação de chamado", "click", "radio", "edita valor");
          }}>
          <FormControlLabel disabled={isLoading} labelPlacement="start" value="none" control={<Radio />} label="Nenhum responsável" />
          <FormControlLabel disabled={isLoading} labelPlacement="start" value="user" control={<Radio />} label={t("profiles.user")} />
          <FormControlLabel disabled={isLoading} labelPlacement="start" value="team" control={<Radio />} label={t("team")} />
        </RadioGroup>
      </FormControl>
      {ticketEdit?.responsible?.type === "team" ? (
        <AutocompleteResponsavel
          label={t("notifications.ticket.EquipeResponsavelConclusao")}
          setValue={setResponsible}
          responsavel={ticketEdit?.responsible?.id || null}
          isLoading={isLoading}
          data={
            (results?.[1]?.data as Team[])
              ?.sort((a: Team, b: Team) => {
                return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1;
              })
              ?.filter((e: Team) => e.status)
              ?.map((ele: Team) => ({
                label: ele.nome,
                value: ele.teamID,
              })) || []
          }
        />
      ) : ticketEdit?.responsible?.type === "user" ? (
        <AutocompleteResponsavel
          label={t("notifications.ticket.UsuarioResponsavelConclusao")}
          setValue={setResponsible}
          responsavel={ticketEdit?.responsible?.id || null}
          isLoading={isLoading}
          data={
            (results?.[0]?.data as userBackObj[])
              ?.sort((a: userBackObj, b: userBackObj) => {
                return a.nome.toLowerCase() > b.nome.toLowerCase() ? 1 : -1;
              })
              ?.filter((e: userBackObj) => e.status === "ativo")
              ?.map((ele: any) => ({
                label: ele.nome,
                value: ele.id,
              })) || []
          }
        />
      ) : null}
      <Typography color="textSecondary" fontWeight="bold" fontSize="small">
        {t("notifications.ticket.regrasPermissao")}
      </Typography>
      <Stack spacing={{ xs: 1, sm: 2 }}
        direction="row"
        useFlexGap
        sx={{ flexWrap: 'wrap' }}
      >
        <SelectResponsavel
          ticketEdit={ticketEdit}
          onLoading={isLoading}
          label="Pode concluir o chamado"
          value={ticketEdit?.permissions?.completionRule}
          setValue={(valor) => {
            handlePermission(valor, "completionRule");
            trackEventMatomo("modal criação de chamado", "click", "select", "seleciona valor");
          }}
        />
        <SelectResponsavel
          ticketEdit={ticketEdit}
          onLoading={isLoading}
          label={t("notifications.ticket.cancelTicket")}
          value={ticketEdit?.permissions?.cancelRule}
          setValue={(valor) => {
            handlePermission(valor, "cancelRule");
            trackEventMatomo("modal criação de chamado", "click", "select", "seleciona valor");
          }}
        />
        <SelectResponsavel
          ticketEdit={ticketEdit}
          onLoading={isLoading}
          label={t("notifications.ticket.closeTicket")}
          value={ticketEdit?.permissions?.closeRule}
          setValue={(valor) => {
            handlePermission(valor, "closeRule");
            trackEventMatomo("modal criação de chamado", "click", "select", "seleciona valor");
          }}
        />
        <SelectResponsavel
          ticketEdit={ticketEdit}
          onLoading={isLoading}
          label={t("notifications.ticket.refuseTicket")}
          value={ticketEdit?.permissions?.rejectRule}
          disabled={ticketEdit?.blockRefuse !== true}
          setValue={(valor) => handlePermission(valor, "rejectRule")}
        />
        <SelectResponsavel
          ticketEdit={ticketEdit}
          onLoading={isLoading}
          label="Quem pode reabrir"
          value={ticketEdit?.permissions?.reopenRule}
          disabled={ticketEdit?.canReopen !== true}
          setValue={(valor) => handlePermission(valor, "reopenRule")}
        />
        <SelectResponsavel
          ticketEdit={ticketEdit}
          onLoading={isLoading}
          label="Quem pode aprovar"
          value={ticketEdit?.permissions?.aproveRule}
          disabled={ticketEdit?.aprove !== true}
          setValue={(valor) => handlePermission(valor, "aproveRule")}
        />
        <SelectResponsavel
          ticketEdit={ticketEdit}
          onLoading={isLoading}
          label={t("notifications.ticket.canChangePriority")}
          value={ticketEdit?.permissions?.changePriorityRule}
          setValue={(valor) => handlePermission(valor, "changePriorityRule")}
        />
      </Stack>
      <Typography color="textSecondary" fontWeight="bold" fontSize="small">
        {t("notifications.ticket.SLA")}
      </Typography>
      {(['firstAttendance', 'startProgress', 'completion'] as slaPriority[]).map((slaKey) => (
        <Accordion key={slaKey} defaultExpanded={slaKey === 'firstAttendance'}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`ticket-sla-${slaKey}-content`} id={`ticket-sla-${slaKey}-header`}>
            <Typography fontWeight={600}>{t(`notifications.ticket.${slaKey}`)}</Typography>
          </AccordionSummary>
          <AccordionDetails id={`ticket-sla-${slaKey}-content`}>
            <Stack direction="row" justifyContent="flex-start" alignItems="flex-start" spacing={2} divider={<Divider orientation="vertical" flexItem />}>
              <SLAInput slaKey={slaKey} label={t("notifications.myTickets.low")} valor={ticketEdit?.prioritySLA?.low?.[slaKey] ?? null} onChange={(value, key) => handlePriority(value, ticketEdit?.prioritySLA?.medium?.[key] ?? null, ticketEdit?.prioritySLA?.high?.[key] ?? null, key)} />
              <SLAInput slaKey={slaKey} label={t("notifications.myTickets.medium")} valor={ticketEdit?.prioritySLA?.medium?.[slaKey] ?? null} onChange={(value, key) => handlePriority(ticketEdit?.prioritySLA?.low?.[key] ?? null, value, ticketEdit?.prioritySLA?.high?.[key] ?? null, key)} />
              <SLAInput slaKey={slaKey} label={t("notifications.myTickets.high")} valor={ticketEdit?.prioritySLA?.high?.[slaKey] ?? null} onChange={(value, key) => handlePriority(ticketEdit?.prioritySLA?.low?.[key] ?? null, ticketEdit?.prioritySLA?.medium?.[key] ?? null, value, key)} />
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Stack>
  );
}

export default function NewTicketModal({ isEdit, setIsEdit, ticketEdit, setTicketEdit, openModal, setOpenModal }: props) {
  const { t } = useTranslation("translation");
  const [value, setValue] = useState(0);

  const hasIncompleteSLA = () => ticketEdit?.prioritySLA
    ? Object.values(ticketEdit.prioritySLA).some(priority => Object.values(priority).some(value => value === null))
    : false;

  const toTicketPayload = (draft: newTicketObjType): Omit<TicketModel, "id"> => {
    if (draft.prioritySLA && Object.values(draft.prioritySLA).some(priority => Object.values(priority).some(value => value === null || value <= 0))) {
      throw new Error("Preencha todos os tempos de SLA com valores maiores que zero ou deixe a configuração vazia para usar o padrão da empresa");
    }

    return {
      ...draft,
      permissions: { ...emptyPermissions(), ...draft.permissions },
      prioritySLA: draft.prioritySLA as TicketModel["prioritySLA"],
    };
  };


  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    trackEventMatomo("modal criação de chamado", "click", "aba", newValue === 0 ? "informações básicas" : "configurações do chamado");
    setValue(newValue);
  };

  const { mutate, isPending } = useMutation({
    mutationKey: ["TICKET_MODEL"],
    mutationFn: async () => {
      try {
        const promise = toast.promise(async () => {
          const token = await AttToken();
          if (!token) throw new Error("Token not found");
          if (!ticketEdit) throw new Error("Ticket edit is undefined");
          const isUpdating = Boolean(ticketEdit.id);
          const ticket = toTicketPayload(ticketEdit);
          if (isUpdating) {
            const response = await chamadoService.updateModel(token, { ...ticket, id: ticketEdit.id as string });
            if (!response) throw new Error("No response from server");
            return response;
          } else {
            const response = await chamadoService.createModel(token, ticket);
            if (!response) throw new Error("No response from server");
            return response;
          }
        }, {
          pending: isEdit ? `${t("notifications.ticket.AtualizandoModelo")}` : `${t("notifications.ticket.CriandoNovoModelo")}`,
          success: isEdit ? `${t("notifications.ticket.ModeloAlterado")}` : `${t("notifications.ticket.ModeloCriado")}`,
          error: isEdit ? `${t("notifications.ticket.ErroAoAlterar")}` : `${t("notifications.ticket.ErroAoCriarModelo")}`,
        }, { toastId: "TICKET_MODEL" });
        return promise;
      } catch (err) {
        throw err;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["GET_MODEL_LIST"] });
      setOpenModal(false);
      if (setIsEdit) {
        setIsEdit(false);
      }
    }
  });

  function close() {
    trackEventMatomo("modal criação de chamado", "click", "button", "fecha modal");
    setOpenModal(false);
    if (setIsEdit) {
      setIsEdit(false);
    }
  }

  const submit = () => {
    trackEventMatomo("modal criação de chamado", "click", "button", "salva");
    mutate();
  };

  const disabled = (): boolean => {
    return !ticketEdit || ticketEdit.name === "" || ticketEdit.message === "" || ticketEdit.serviceType === "" || !ticketEdit.setsList?.length || Boolean(ticketEdit.responsible && !ticketEdit.responsible.id) || hasIncompleteSLA() || isPending;
  };

  return (
    <Dialog fullWidth maxWidth="md" open={openModal} onClose={close} scroll={"paper"}>
      <DialogTitle>{!(isEdit ?? Boolean(ticketEdit?.id)) ? t("notifications.ticket.criarModelo") : t("notifications.ticket.editarModelo")}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={close}
        sx={(theme) => ({
          position: "absolute",
          right: 8,
          top: 8,
          color: theme.palette.grey[500],
        })}>
        <CloseIcon />
      </IconButton>
      <DialogContent dividers={true}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs indicatorColor="secondary" textColor="secondary" value={value} onChange={handleChange} aria-label="basic tabs example">
            <Tab label={t("notifications.ticket.basicInfo")} {...a11yProps(0)} />
            <Tab label={t("notifications.ticket.ticketConfigInfo")} {...a11yProps(1)} />
          </Tabs>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <BasicInfoTab setTicketEdit={setTicketEdit} ticketEdit={ticketEdit} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <ConfigTicketTab setTicketEdit={setTicketEdit} ticketEdit={ticketEdit} />
        </CustomTabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={submit} disabled={disabled()}>
          {t("save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
